import { Blend, Contrast, DynamicColor, Hct, TonalPalette, argbFromHex, hexFromArgb, themeFromSourceColor } from "@material/material-color-utilities";

import { Controller } from "@hotwired/stimulus";

// 4.5 is the minimum contrast ratio for text following WCAG AA standards.
const MINIMUM_CONTRAST_RATIO = 4.5;
// Contrast ratio Malong uses when generating "on" colors for exact theme
// colors; keep in sync with getOnColor in malong/src/shared/themes/CustomTheme.tsx.
const ON_COLOR_CONTRAST_RATIO = 13;

// Generates the "on" color for a background color (usually used for text on
// that background color) the same way Malong does for exact theme colors, so
// the previews match the real pages.
const getOnColor = (argb) => {
  const tone = Hct.fromInt(argb).tone;
  const foregroundTone = DynamicColor.foregroundTone(tone, ON_COLOR_CONTRAST_RATIO);
  return TonalPalette.fromInt(argb).tone(foregroundTone);
};
const THEMES = ["light", "dark"];
const COLOR_TYPES = ["accent", "secondary"];

const interpolate = (template, values) =>
  Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`%{${key}}`, value),
    template,
  );

// Handles the custom theme colors from branding in two contexts:
//
// - Previews (interactions, registration fields) declare the color values and
//   get M3 color schemes applied as --theme-* CSS variables on the body.
// - The custom theme colors form declares the contrast config and targets and
//   gets an accessibility contrast warning for the exact theme colors.
export default class extends Controller {
  static targets = [
    "lightButton",
    "darkButton",
    "contrastAccentWarning",
    "contrastSecondaryWarning",
    "themeToggle",
    "webinarTheme",
  ];
  static values = {
    accentColor: String,
    secondaryColor: String,
    active: Boolean,
    applyExactThemeColors: Boolean,
    defaultTheme: { type: String, default: "light" },
    contrastConfig: Object,
  };

  connect() {
    // Only pages that declare the active value want the --theme-* CSS
    // variables; the custom theme colors form previews through the Malong
    // embeds instead.
    if (this.hasActiveValue) {
      this.materialTheme = this.buildMaterialTheme();
      this.setTheme(this.defaultThemeValue);
    }

    // Saving either branding form triggers a Turbo morph refresh, which resets
    // the notice to its server-rendered (hidden, empty) state without
    // reconnecting this controller. Re-run the check after every morph so the
    // notice reflects the newly saved webinar theme settings and colors.
    this.boundCheckContrast = this.checkContrast.bind(this);
    document.addEventListener("turbo:morph", this.boundCheckContrast);
    this.checkContrast();
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.boundCheckContrast);
  }

  light() {
    this.setTheme("light");
  }

  dark() {
    this.setTheme("dark");
  }

  updateButtonStates(theme) {
    if (this.hasLightButtonTarget && this.hasDarkButtonTarget) {
      const isDark = theme === "dark";
      this.lightButtonTarget.disabled = !isDark;
      this.darkButtonTarget.disabled = isDark;
    }
  }

  setTheme(theme) {
    if (this.activeValue && this.materialTheme) {
      this.applyCustomColors(theme);
    } else {
      this.applyDefaultColors(theme);
    }
    this.updateButtonStates(theme);
  }

  buildMaterialTheme() {
    const accent = this.accentColorValue;
    const secondary = this.secondaryColorValue;

    if (!accent || !secondary) return null;

    return themeFromSourceColor(argbFromHex(accent), [
      { blend: false, name: "tertiary", value: argbFromHex(secondary) },
    ]);
  }

  applyDefaultColors(theme) {
    const prefix = theme === "dark" ? "dark" : "light";
    const body = document.body;

    const colorProperties = {
      "--theme-primary": `var(--${prefix}-primary)`,
      "--theme-on-primary": `var(--${prefix}-on-primary)`,
      "--theme-secondary": `var(--${prefix}-secondary)`,
      "--theme-surface": `var(--${prefix}-surface-at-4)`,
      "--theme-on-surface": `var(--${prefix}-on-surface)`,
      "--theme-outline": `var(--${prefix}-outline)`,
      "--theme-surface-at-4": `var(--${prefix}-surface-at-5)`,
    };

    for (const [prop, value] of Object.entries(colorProperties)) {
      body.style.setProperty(prop, value);
    }
  }

  applyCustomColors(theme) {
    const scheme = this.materialTheme.schemes[theme];
    const body = document.body;

    // With exact theme colors the accent is used as-is instead of the scheme's
    // generated primary (like Malong does), so the on-color is generated too.
    const exact = this.applyExactThemeColorsValue;
    const primary = exact ? argbFromHex(this.accentColorValue) : scheme.primary;
    const onPrimary = exact ? getOnColor(primary) : scheme.onPrimary;

    const colorProperties = {
      "--theme-primary": hexFromArgb(primary),
      "--theme-on-primary": hexFromArgb(onPrimary),
      "--theme-secondary": hexFromArgb(scheme.secondary),
      "--theme-surface": hexFromArgb(scheme.surface),
      "--theme-on-surface": hexFromArgb(scheme.onSurface),
      "--theme-outline": hexFromArgb(scheme.outline),
    };

    for (const [prop, value] of Object.entries(colorProperties)) {
      body.style.setProperty(prop, value);
    }

    const surfaceAt4 = Blend.cam16Ucs(scheme.surface, scheme.primary, 0.12);
    body.style.setProperty("--theme-surface-at-4", hexFromArgb(surfaceAt4));
  }

  checkContrast() {
    if (!this.hasContrastAccentWarningTarget && !this.hasContrastSecondaryWarningTarget) return;

    this.renderNotices(this.contrastResult());
  }

  // Reports the accessibility contrast check for the exact accent and secondary
  // colors: for each color, the themes in which it is hard to see. Only themes
  // a viewer can actually reach are checked. Generated (non-exact) themes
  // always keep enough contrast, so the check only runs when exact colors
  // apply. Returns null when nothing to show.
  contrastResult() {
    if (!this.contrastCheckApplies) return null;

    const colorsByType = {
      accent: this.colorValue("accent"),
      secondary: this.colorValue("secondary"),
    };

    if (!Object.values(colorsByType).every((color) => this.isValidHex(color))) {
      return null;
    }

    // Surface is derived from the accent color (the theme's source color) for
    // both the light and dark schemes; the secondary color does not affect it.
    const exactColorTheme = themeFromSourceColor(argbFromHex(colorsByType.accent));
    const reachableThemes = this.reachableThemes();
    const failingThemesByType = Object.fromEntries(
      COLOR_TYPES.map((type) => {
        const colorTone = Hct.fromInt(argbFromHex(colorsByType[type])).tone;
        const failing = reachableThemes.filter((theme) => {
          const surfaceTone = Hct.fromInt(exactColorTheme.schemes[theme].surface).tone;
          return Contrast.ratioOfTones(colorTone, surfaceTone) < MINIMUM_CONTRAST_RATIO;
        });
        return [type, failing];
      }),
    );

    if (Object.values(failingThemesByType).every((themes) => themes.length === 0)) return null;

    return failingThemesByType;
  }

  // Themes a viewer can reach with the current form settings: both themes when
  // the theme toggle is on, otherwise just the selected webinar theme. Reading
  // the live inputs keeps the notice in sync while the user edits the form,
  // since the webinar theme settings and the colors live in the same form.
  reachableThemes() {
    if (!this.hasThemeToggleTarget || !this.hasWebinarThemeTarget) return THEMES;

    return this.themeToggleTarget.checked ? THEMES : [this.webinarThemeTarget.value];
  }

  renderNotices(failingThemesByType) {
    this.renderNotice(this.contrastAccentWarningTarget, failingThemesByType?.accent ?? []);
    this.renderNotice(this.contrastSecondaryWarningTarget, failingThemesByType?.secondary ?? []);
  }

  renderNotice(target, failingThemes) {
    const message = this.contrastWarning(failingThemes);

    target.textContent = message;
    target.hidden = !message;
  }

  contrastWarning(failingThemes) {
    if (failingThemes.length === 0) return "";
    if (failingThemes.length === THEMES.length) return this.contrastConfigValue.warningBoth;

    return interpolate(this.contrastConfigValue.warningSingle, {
      theme: this.contrastConfigValue.themes[failingThemes[0]],
    });
  }

  // The check only makes sense when a custom theme is enabled and exact colors
  // are being applied.
  get contrastCheckApplies() {
    return this.isChecked("theme-color#enable") && this.isChecked("theme-color#enableExact");
  }

  colorValue(type) {
    const input = this.element.querySelector(`input[type="text"][data-type="${type}"]`);
    const colorInput = this.element.querySelector(`input[type="color"][data-type="${type}"]`);
    return input?.value || colorInput?.value || "";
  }

  isChecked(action) {
    const checkbox = this.element.querySelector(`input[type="checkbox"][data-action~="${action}"]`);
    return Boolean(checkbox?.checked);
  }

  isValidHex(value) {
    return /^#[0-9a-fA-F]{6}$/.test(value);
  }
}
